import { useState } from "react";
import { LegalEntityContactRow, LegalEntityContactSaveModel, LegalEntityContactType } from "../LegalEntityContactTab.types";
import { useTranslation } from "@localization/useTranslation";
import { post, put } from "@common/api/apiShared";
import { useValidator } from "@common/hooks/validation";
import { setFirstLetterToLowerCase } from "@common/stringOperations";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { getEnumValues } from "@common/enumOperations";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { usePane } from "../../../main/panes/paneLayout/usePane";
import { usePanes } from "../../../main/panes/usePanes";
import { useAuditGroupDraftSelection } from "../../../legalEntityAuditGroup/legalEntityAuditGroupDraftOverview/useAuditGroupDraftSelection";
import { LegalEntityContactPaneProps } from "./LegalEntityContactPane.types";

export function useLegalEntityContactPane(props: LegalEntityContactPaneProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { close, setHasUnsavedChanges } = usePane();
    const { refetchPageBehindPanes } = usePanes();
    const { selectContactForLegalEntity } = useAuditGroupDraftSelection(props.auditGroupId ?? "");

    const isEdit = props.contact !== undefined;

    const [contact, setContact] = useState<LegalEntityContactSaveModel>(getLegalEntityContactSaveModel(props.contact));

    const validation = useValidator<LegalEntityContactSaveModel>((validators) => ({
        name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        email: validators.validateEmail((item) => item.email, translateString("email")),
    }));

    validation.check(contact);

    const createMutation = useDotLegalMutation(createContactApi);
    const editMutation = useDotLegalMutation(editContactApi);

    const createContact = () => {
        createMutation.mutateAsync(contact, {
            onSuccess: async (response: any) => {
                if (props.auditGroupId) {
                    await selectContactForLegalEntity(props.legalEntityId, response.value().contactId);
                }
                onContactSaved("contactCreated");
            },
        });
    };

    const editContact = () => {
        editMutation.mutateAsync(contact, {
            onSuccess: () => onContactSaved("contactUpdated"),
        });
    };

    const onContactSaved = (snackbarTextKey: string) => {
        snackbar.show(translateString(snackbarTextKey));
        refetchPageBehindPanes();
        close();
    };

    const onSaveClick = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (isEdit) {
            editContact();
        } else {
            createContact();
        }
    };

    function updateContact(changes: Partial<LegalEntityContactSaveModel>) {
        setHasUnsavedChanges(true);
        setContact({ ...contact, ...changes });
    }

    const modelUpdater = {
        onNameChange: (name: string) => updateContact({ name }),
        onEmailChange: (email: string) => updateContact({ email }),
        onPhoneNumberChange: (phoneNumber: string) => updateContact({ phoneNumber }),
        onPrimaryContactChange: (primaryContact: boolean) => updateContact({ primaryContact }),
        onContactTypesChange: (types: Array<string>) => updateContact({ contactTypes: types.map((x) => Number(x)) }),
    };

    return {
        contact,
        modelUpdater,
        onSaveClick,
        validation,
        isEdit,
        isSaving: createMutation.isLoading || editMutation.isLoading,
    };

    function createContactApi(model: LegalEntityContactSaveModel) {
        return post(`/LegalEntityContacts/${props.legalEntityId}/`, model);
    }

    function editContactApi(model: LegalEntityContactSaveModel) {
        return put(`/LegalEntityContacts/${props.legalEntityId}/${props.contact!.id}`, model);
    }
}

function getLegalEntityContactSaveModel(model?: LegalEntityContactRow) {
    return {
        name: model?.name ?? "",
        email: model?.email ?? "",
        phoneNumber: model?.phoneNumber ?? "",
        primaryContact: model?.primaryContact ?? false,
        contactTypes: model?.contactTypes ?? [],
    };
}

export const contactTypeOptions = (translateString: any) => {
    return getEnumValues(LegalEntityContactType).map((x) => {
        var name = translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()));
        return { name: name, id: x.toString() };
    });
};
